import { Box, CircularProgress, Typography, useTheme } from '@mui/material'
import { useShowBottomNav } from '@hooks/useShowBottomNav'
import { useShowMiniSessionBar } from '@hooks/useShowMiniSessionBar'
import { useShellOverlayMetrics } from '@components/layout/shellOverlayMetrics'
import {
  BACKEND_WAKE_BANNER_HEIGHT_PX,
  shellFixedBottomOffsetCss,
  shellOverlayZIndex,
} from '@components/layout/shellOverlayStack'

type BackendWakeBannerProps = {
  visible: boolean
  /** When true, in-flow inside ShellBottomAlertStack (no own fixed positioning). */
  embedded?: boolean
}

/** Compact wake UI above bottom nav; usable at 375px width. */
const BackendWakeBanner: React.FC<BackendWakeBannerProps> = ({ visible, embedded = false }) => {
  const theme = useTheme()
  const showBottomNav = useShowBottomNav()
  const showMiniBar = useShowMiniSessionBar()
  const { keyboardInsetPx } = useShellOverlayMetrics()
  const z = shellOverlayZIndex(theme)

  if (!visible) {
    return null
  }

  return (
    <Box
      role="status"
      aria-live="polite"
      data-testid="backend-wake-banner"
      sx={{
        ...(embedded
          ? {}
          : {
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: shellFixedBottomOffsetCss(
                {
                  bottomNav: showBottomNav,
                  miniSessionBar: showMiniBar,
                },
                { keyboardInsetPx },
              ),
              zIndex: z.backendWakeBanner,
            }),
        minHeight: BACKEND_WAKE_BANNER_HEIGHT_PX,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        boxShadow: embedded ? 'none' : theme.shadows[4],
      }}
    >
      <CircularProgress size={28} aria-hidden />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle2" component="p" noWrap>
          Starting up…
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Waking the server. This can take up to a minute.
        </Typography>
      </Box>
    </Box>
  )
}

export default BackendWakeBanner
